/**
 * Light fitting types: which curated glyph a light gets from its name, and which
 * sub-group ("Ceiling & downlights", "Lamps", "Accent", "Outside") it sits in on a
 * busy room page. Pure data and logic (no React) so tests and scripts can run it.
 *
 * iconFor() (resolve.ts) uses lightFromName() after the admin override and the HA
 * icon; the room page groups by lightGroupOf(icon), so tile icon and heading
 * always agree.
 */

/** Every light glyph in the set (each has an `-on` variant). */
export const LIGHT_ICONS = [
  'ceiling', 'downlights', 'spot', 'pendant', 'chandelier', 'skylight-light',
  'lamp', 'bedside', 'floor-lamp', 'desk-lamp', 'nightlight',
  'sconce', 'strip', 'mirror-light', 'cabinet-light', 'step-light',
  'lantern',
  'bulb',
] as const;
export type LightIcon = (typeof LIGHT_ICONS)[number];

export const LIGHT_ICON_LABEL: Record<LightIcon, string> = {
  ceiling: 'Ceiling', downlights: 'Downlights', spot: 'Spotlight', pendant: 'Pendant', chandelier: 'Chandelier',
  'skylight-light': 'Skylight', lamp: 'Table lamp', bedside: 'Bedside', 'floor-lamp': 'Floor lamp',
  'desk-lamp': 'Desk lamp', nightlight: 'Nightlight', sconce: 'Wall light', strip: 'LED strip',
  'mirror-light': 'Mirror', 'cabinet-light': 'Under-cabinet', 'step-light': 'Stair lights', lantern: 'Outdoor',
  bulb: 'Bulb',
};

const LIT = new Set<string>(LIGHT_ICONS);
export const isLightIcon = (icon: string): icon is LightIcon => LIT.has(icon);

/**
 * Name keywords → fitting, first match wins. Run on "name shortName" lower-cased.
 * Order matters: specific fittings before the words they contain ("floor lamp"
 * before "lamp", "desk" before "lamp", "pendant" before "ceiling").
 */
const NAME_RULES: Array<[RegExp, LightIcon]> = [
  [/night ?light|\bnight\b/, 'nightlight'],
  [/mirror|vanity/, 'mirror-light'],
  [/cabinet|cupboard|under ?counter|worktop|plinth|kick ?board|pantry/, 'cabinet-light'],
  [/\bstrips?\b|\btape\b|\bleds?\b|\bcove\b|bookcase|bookshelf|shel(f|ves)|\bniche\b|backlight|chamber light|\bunder ?bed\b/, 'strip'],
  [/skylight|roof ?light|lantern light|\bblind light/, 'skylight-light'],
  [/chandelier/, 'chandelier'],
  [/pendant|\bisland\b/, 'pendant'],
  [/down ?lights?|\bspots?\b|spotlight|recessed|\brow ?\d*\b|\bcans?\b|\btrack\b|\bgimbal/, 'downlights'],
  [/\bdesk\b|reading lamp|\bstudy lamp/, 'desk-lamp'],
  [/bedside|night ?stand|\bbed ?(left|right)\b/, 'bedside'],
  [/floor ?(lamp|light|standing)?\b|standard lamp|\buplighter|\barc lamp/, 'floor-lamp'],
  [/\blamps?\b/, 'lamp'],
  [/\bwall\b|sconce|picture light/, 'sconce'],
  [/\bstairs?\b|\bsteps?\b|stairwell/, 'step-light'],
  [/outside|outdoor|exterior|garden|porch|balcony|patio|terrace|decking|driveway|\bdrive\b|\bpath\b|lantern|flood ?light|security light|front door light|\bgate\b|pergola/, 'lantern'],
  [/ceiling|flush|overhead|\bmain\b|centre|center|\btop light\b/, 'ceiling'],
];

/** Indicator LEDs on switches, routers and controllers are not room lights; let them stay generic. */
const INDICATOR = /(switch|status|indicator|power|router|extender|controller|doorbell|tablet|screen)\s*leds?\b|\bscreen\b|\btablet\b/;

/**
 * The fitting a light's name describes, or undefined when it says nothing (the caller
 * falls back to 'bulb'). `shortName` is the name inside its room; a bare "Light" there
 * ("Downstairs loo" → "Light") is the room's main light, i.e. the ceiling.
 */
export function lightFromName(name: string | null | undefined, shortName?: string | null): LightIcon | undefined {
  const text = [name, shortName].filter(Boolean).join(' ').toLowerCase().replace(/[_.]+/g, ' ');
  if (!text.trim() || INDICATOR.test(text)) return undefined;
  for (const [re, icon] of NAME_RULES) if (re.test(text)) return icon;
  if (shortName && /^\s*lights?\s*$/i.test(shortName)) return 'ceiling';
  return undefined;
}

// ---------------------------------------------------------------- sub-groups

export type LightGroupId = 'ceiling' | 'lamps' | 'accent' | 'outside' | 'other';

export const LIGHT_GROUPS: Array<{ id: LightGroupId; title: string; icon: string }> = [
  { id: 'ceiling', title: 'Ceiling & downlights', icon: 'downlights' },
  { id: 'lamps', title: 'Lamps', icon: 'lamp' },
  { id: 'accent', title: 'Accent', icon: 'strip' },
  { id: 'outside', title: 'Outside', icon: 'lantern' },
  { id: 'other', title: 'Other lights', icon: 'bulb' },
];

const GROUP_OF: Record<LightIcon, LightGroupId> = {
  ceiling: 'ceiling', downlights: 'ceiling', spot: 'ceiling', pendant: 'ceiling', chandelier: 'ceiling', 'skylight-light': 'ceiling',
  lamp: 'lamps', bedside: 'lamps', 'floor-lamp': 'lamps', 'desk-lamp': 'lamps', nightlight: 'lamps',
  sconce: 'accent', strip: 'accent', 'mirror-light': 'accent', 'cabinet-light': 'accent', 'step-light': 'accent',
  lantern: 'outside',
  bulb: 'other',
};

/** Sub-group for a (base, state-free) icon name. Non-light overrides land in "Other lights". */
export const lightGroupOf = (icon: string): LightGroupId => (isLightIcon(icon) ? GROUP_OF[icon] : 'other');

/** Rooms with more lights than this get sub-group headings; smaller rooms keep one grid. */
export const LIGHT_SUBGROUP_MIN = 7;

/** Natural, case-insensitive name order: "Row 2" before "Row 10". */
export const naturalCompare = (a: string, b: string): number =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
